/**
 * F2 · chips de precio en el mapa de la lista compartida (`/shared/list/:token`).
 *
 * Un chip por registro de la lista (UF o lote sin UF; las listas no tienen CAD),
 * armado de las MISMAS cards del panel (`buildBrandedListingItems` sobre los
 * lotes ya filtrados): misma clave (`item.key`), mismos filtros y solo lo que el
 * share ya expone. Funciones puras: el hook `useSharedRecordChips` las pinta con
 * `maplibregl.Marker`.
 *
 * - Texto: precio compacto (`formatCompactRecordPrice`, «USD 120k»); sin precio,
 *   tipo corto («Depto», «Lote»). No se ocultan.
 * - Varias UF en la coordenada del lote (no hay coordenada por UF): abanico por
 *   punto (`groupRecordsForMap`) apilado hacia arriba. La flecha del chip de
 *   abajo marca el lote; el tooltip de publicación sale arriba de todo el
 *   abanico.
 * - Zoom bajo: los puntos cuyo abanico se solapa en pantalla se juntan en un
 *   clúster con contador (`layoutRecordChips`).
 * @see docs/maps/shared-list-view.md («Chips de precio (F2)»)
 */
import {
  formatCompactRecordPrice,
  groupRecordsForMap,
  RECORD_KIND,
} from './recordCards';
import { getLotRepresentativeLatLng } from '../hooks/useLotSelection';
import { normalizeBuildingType } from './ufAttributeFormUtils';
import { resolveUnitOperation } from './userLotFormUtils';
import { formatListingPrice, OPERATION_LABEL } from './listingCoreConstants';

/**
 * Rollback sin deploy de código: `REACT_APP_SHARE_PRICE_CHIPS=off` vuelve a los
 * pines verdes por lote (`useListPinsLayer`). Default: chips.
 */
export const isSharedListPriceChipsEnabled = (raw = process.env.REACT_APP_SHARE_PRICE_CHIPS) => (
  String(raw ?? '').trim().toLowerCase() !== 'off'
);

/** Medidas del chip en px (CSS `.terrio-record-chip` en Map.css). */
export const CHIP_SIZE = Object.freeze({ width: 76, height: 22, gapX: 4, gapY: 4 });
/** Alto de la flecha bajo el chip que marca el lote. */
export const CHIP_TAIL = 6;
/** Aire entre el tope del abanico y el tooltip de publicación. */
export const TOOLTIP_GAP = 10;
/** Filas por columna del abanico. */
export const FAN_ROWS = 5;
/** Más registros que esto en un punto: se muestran FAN_MAX - 1 chips + «+N». */
export const FAN_MAX = 10;
/** Desde este zoom no se agrupa en clúster (los abanicos pueden tocarse). */
export const CLUSTER_MAX_ZOOM = 17;
/** Radio del clúster en px. */
export const CLUSTER_SIZE = 36;

const SHORT_TYPE_LABEL = {
  departamento: 'Depto',
  casa: 'Casa',
  ph: 'PH',
  comercio: 'Local',
  terreno: 'Lote',
};

const text = (value) => {
  if (value == null) return null;
  const out = String(value).trim();
  return out || null;
};

const toNumber = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Tipo corto del chip sin precio: UF → tipo de propiedad («Depto», «Casa») o
 * «UF»; lote → «Lote».
 */
export function getShortRecordTypeLabel(kind, propertyType) {
  if (kind === RECORD_KIND.LOT) return 'Lote';
  const key = normalizeBuildingType(propertyType) || text(propertyType)?.toLowerCase();
  return (key && SHORT_TYPE_LABEL[key]) || 'UF';
}

const unitField = (unit, key) => {
  if (!unit) return null;
  const attrs = unit.private_attributes && typeof unit.private_attributes === 'object'
    ? unit.private_attributes
    : {};
  return unit[key] ?? attrs[key] ?? null;
};

/**
 * Card del panel → registro del mapa (forma de `recordCards`: `record_key`,
 * `record_kind`, `group_key`, `lat`/`lng`, `price`/`currency`). Coordenada: la
 * del lote (`getLotRepresentativeLatLng`: lat/lng, de una UF o el centro del
 * polígono); sin coordenada → null.
 * @param {object} item - de `buildBrandedListingItems`
 * @returns {object|null}
 */
export function sharedItemToMapRecord(item) {
  if (!item?.key || !item.lotId) return null;
  const point = getLotRepresentativeLatLng(item.lot)
    || getLotRepresentativeLatLng(item.unit);
  if (!point) return null;
  const isUnit = item.itemType === 'unit';
  const content = isUnit ? null : (item.lot?.lot_user_content ?? null);
  const priceSource = isUnit ? item.unit : content;
  const price = toNumber(unitField(priceSource, 'price'));
  const currency = text(unitField(priceSource, 'currency'))?.toUpperCase() || null;
  const propertyType = isUnit ? text(unitField(item.unit, 'building_type')) : 'terreno';
  const operation = isUnit ? resolveUnitOperation(item.unit) : text(content?.operation);
  const kind = isUnit ? RECORD_KIND.UNIT : RECORD_KIND.LOT;
  const compactPrice = price != null && price > 0 ? formatCompactRecordPrice(price, currency) : null;
  return {
    record_key: item.key,
    record_kind: kind,
    lot_id: String(item.lotId),
    user_lot_id: isUnit && item.userLotId != null ? String(item.userLotId) : null,
    group_key: `lot:${item.lotId}`,
    lat: point.lat,
    lng: point.lng,
    price: compactPrice ? price : null,
    currency: compactPrice ? (currency || 'USD') : null,
    property_type: propertyType,
    operation: operation || null,
    hasPrice: Boolean(compactPrice),
    label: compactPrice || getShortRecordTypeLabel(kind, propertyType),
    item,
  };
}

/**
 * Puntos del mapa: cards → registros → un punto por lote/coordenada con sus
 * registros en abanico (`fanIndex` / `fanCount`). Orden = orden del panel.
 * @param {object[]} items
 */
export function buildSharedRecordMapPoints(items) {
  const records = (Array.isArray(items) ? items : [])
    .map(sharedItemToMapRecord)
    .filter(Boolean);
  return groupRecordsForMap(records);
}

/** Alto del marker: la pastilla, y la flecha si este chip marca el lote. */
export function markerBoxHeight(tail) {
  return CHIP_SIZE.height + (tail ? CHIP_TAIL : 0);
}

/**
 * Abanico anclado por la base (`anchor: 'bottom'`). El offset es el borde
 * inferior del marker respecto del punto: Y negativo = arriba. El primer
 * registro del panel queda arriba; el más cercano al lote lleva la flecha y
 * su punta cae en la coordenada. Una sola flecha por punto.
 * @param {number} count
 * @returns {Array<{ offset: [number, number], tail: boolean }>}
 */
export function computeFanOffsets(count, chipWidth = CHIP_SIZE.width) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (n === 0) return [];
  const cols = Math.ceil(n / FAN_ROWS);
  const rows = Math.ceil(n / cols);
  const stepX = chipWidth + CHIP_SIZE.gapX;
  const stepY = CHIP_SIZE.height + CHIP_SIZE.gapY;
  const tailCol = Math.floor((cols - 1) / 2);
  const tailRow = rows - 1;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const col = Math.floor(i / rows);
    const row = i % rows;
    const tail = col === tailCol && row === tailRow;
    const pillsBelow = rows - 1 - row;
    const dy = tail ? -(pillsBelow * stepY) : -(CHIP_TAIL + pillsBelow * stepY);
    const dx = (col - (cols - 1) / 2) * stepX;
    out.push({
      offset: [Math.round(dx) || 0, Math.round(dy) || 0],
      tail,
    });
  }
  return out;
}

/**
 * Offset del tooltip (ancla abajo) para que la card quede arriba de todo el
 * abanico, alineada en X con el chip. `stackTop` es el borde superior del
 * abanico en px respecto del punto.
 * @param {{ offset?: [number, number], stackTop?: number }} entry
 * @returns {[number, number]}
 */
export function chipTooltipOffset(entry) {
  const dx = entry?.offset?.[0] ?? 0;
  const top = Number.isFinite(entry?.stackTop)
    ? entry.stackTop
    : (entry?.offset?.[1] ?? 0) - CHIP_SIZE.height;
  return [Math.round(dx), Math.round(top - TOOLTIP_GAP)];
}

/** Entradas visibles de un punto: chips (hasta FAN_MAX) y, si sobran, «+N». */
export function fanEntriesForPoint(point, chipWidth = CHIP_SIZE.width) {
  const records = point.records || [];
  const overflow = records.length > FAN_MAX;
  const shown = overflow ? records.slice(0, FAN_MAX - 1) : records;
  const total = shown.length + (overflow ? 1 : 0);
  const slots = computeFanOffsets(total, chipWidth);
  const stackTop = Math.min(...slots.map((slot) => slot.offset[1] - markerBoxHeight(slot.tail)));
  const toEntry = (extra, index) => ({
    ...extra,
    lngLat: [point.lng, point.lat],
    offset: slots[index].offset,
    tail: slots[index].tail,
    stackTop,
    pointKey: point.point_key,
  });
  const entries = shown.map((record, index) => toEntry({
    type: 'chip',
    key: record.record_key,
    record,
  }, index));
  if (overflow) {
    entries.push(toEntry({
      type: 'more',
      key: `more:${point.point_key}`,
      count: records.length - shown.length,
      lotId: records[0]?.lot_id ?? null,
    }, total - 1));
  }
  return { entries, slots };
}

const fanBox = (x, y, slots, chipWidth = CHIP_SIZE.width) => {
  const halfW = chipWidth / 2;
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  slots.forEach(({ offset: [dx, dy], tail }) => {
    const top = y + dy - markerBoxHeight(tail);
    const bottom = y + dy;
    minX = Math.min(minX, x + dx - halfW);
    maxX = Math.max(maxX, x + dx + halfW);
    minY = Math.min(minY, top);
    maxY = Math.max(maxY, bottom);
  });
  return { minX, minY, maxX, maxY };
};

const boxesOverlap = (a, b) => a.minX < b.maxX && b.minX < a.maxX && a.minY < b.maxY && b.minY < a.maxY;

/**
 * Qué se dibuja en pantalla: abanicos de chips y, a zoom < CLUSTER_MAX_ZOOM,
 * clústeres donde los abanicos se solaparían. Greedy en orden del panel: un
 * punto que pisa a otro ya colocado se suma a él (que pasa a clúster, en su
 * posición). Solo puntos dentro del viewport (+ margen) → DOM acotado.
 * @param {object[]} points - `buildSharedRecordMapPoints`
 * @param {object} options
 * @param {(lngLat: [number, number]) => { x: number, y: number }} options.project
 * @param {number} options.zoom
 * @param {{ width: number, height: number }} [options.viewport]
 * @param {number} [options.margin]
 * @param {number} [options.chipWidth] - ancho del chip si no es el de `CHIP_SIZE` (comparables
 *   de mercado, con moneda y unidad); abanicos y clústeres lo usan para no solaparse.
 * @returns {object[]} entradas `{ type: 'chip'|'more'|'cluster', key, lngLat, offset, … }`
 */
export function layoutRecordChips(points, {
  project,
  zoom,
  viewport = null,
  margin = 120,
  chipWidth = CHIP_SIZE.width,
} = {}) {
  const canCluster = Number(zoom) < CLUSTER_MAX_ZOOM;
  const placed = [];
  (Array.isArray(points) ? points : []).forEach((point) => {
    const { x, y } = project([point.lng, point.lat]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (viewport && (
      x < -margin || y < -margin || x > viewport.width + margin || y > viewport.height + margin
    )) return;
    const { entries, slots } = fanEntriesForPoint(point, chipWidth);
    const box = fanBox(x, y, slots, chipWidth);
    const hit = canCluster ? placed.find((other) => boxesOverlap(other.box, box)) : null;
    if (hit) {
      hit.points.push(point);
      hit.cluster = true;
      return;
    }
    placed.push({ x, y, box, points: [point], entries, cluster: false });
  });
  const out = [];
  placed.forEach((slot) => {
    if (!slot.cluster) {
      out.push(...slot.entries);
      return;
    }
    const records = slot.points.flatMap((p) => p.records);
    const lngs = slot.points.map((p) => p.lng);
    const lats = slot.points.map((p) => p.lat);
    const first = slot.points[0];
    out.push({
      type: 'cluster',
      key: `cluster:${first.point_key}`,
      lngLat: [first.lng, first.lat],
      offset: [0, 0],
      count: records.length,
      recordKeys: records.map((r) => r.record_key),
      bounds: [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
    });
  });
  return out;
}

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const PROPERTY_TYPE_TOOLTIP = {
  departamento: 'Departamento',
  casa: 'Casa',
  ph: 'PH',
  comercio: 'Comercio',
  terreno: 'Lote',
};

/**
 * Tooltip del chip (hover): foto mini si hay portada, título, «tipo · operación»,
 * precio completo y superficie. Solo datos de la card del share (mismo `item`).
 * @param {object} record - de `sharedItemToMapRecord`
 * @returns {string|null}
 */
export function buildSharedRecordTooltipHtml(record) {
  const item = record?.item;
  if (!item) return null;
  const typeKey = normalizeBuildingType(record.property_type) || text(record.property_type)?.toLowerCase();
  const typeLabel = record.record_kind === RECORD_KIND.LOT
    ? 'Lote'
    : (typeKey && PROPERTY_TYPE_TOOLTIP[typeKey]) || 'Unidad';
  const opLabel = record.operation ? OPERATION_LABEL[record.operation] || null : null;
  const price = record.hasPrice ? formatListingPrice(record.price, record.currency) : null;
  const surface = (item.metas || []).find((meta) => meta?.key === 'surface')?.value || null;
  const title = text(item.title) || text(item.cadastralLabel) || typeLabel;
  const cover = typeof item.coverImage === 'string' && /^https?:\/\//i.test(item.coverImage)
    ? item.coverImage
    : null;
  const rows = [];
  if (cover) {
    rows.push(`<img class="terrio-record-chip-tooltip__cover" src="${escapeHtml(cover)}" alt="" loading="lazy" />`);
  }
  rows.push(`<div class="terrio-cad-share-tooltip__title">${escapeHtml(title)}</div>`);
  rows.push(`<div class="terrio-cad-share-tooltip__muted">${escapeHtml([typeLabel, opLabel].filter(Boolean).join(' · '))}</div>`);
  if (price) rows.push(`<div class="terrio-cad-share-tooltip__price">${escapeHtml(price)}</div>`);
  if (surface) rows.push(`<div class="terrio-cad-share-tooltip__muted">${escapeHtml(surface)}</div>`);
  rows.push('<div class="terrio-cad-share-tooltip__footer">Click para ver detalle <span aria-hidden="true">→</span></div>');
  return `<div class="terrio-cad-share-tooltip__card" data-record-key="${escapeHtml(record.record_key)}">${rows.join('')}</div>`;
}

/**
 * Tooltip de publicación para el hover de una card del detalle de lista
 * (sin chips en el mapa). Misma card que el chip del share. Sin coordenada
 * no hay ancla.
 * @param {object|null} item - card (`toRecordCard` o item del share)
 * @param {object|null} [lot]
 * @returns {{ html: string, lngLat: [number, number], variant: 'share' }|null}
 */
export function buildListDetailHoverPopup(item, lot = null) {
  const point = getLotRepresentativeLatLng(item?.lot)
    || getLotRepresentativeLatLng(item?.unit)
    || getLotRepresentativeLatLng(lot);
  if (!point || !item) return null;
  const record = sharedItemToMapRecord(item);
  const html = record && buildSharedRecordTooltipHtml(record);
  if (!html) return null;
  return { html, lngLat: [point.lng, point.lat], variant: 'share' };
}

/**
 * Clave de card / chip seleccionada a partir de la selección de la página
 * (`selectedLot`) o de la card marcada con Ubicar.
 * @returns {string|null}
 */
export function resolveSelectedRecordKey({ selectedLot = null, locatedLotId = null, locatedUserLotId = null } = {}) {
  if (selectedLot) {
    if (selectedLot.item_type === 'units_list') return null;
    if (selectedLot.item_type === 'lot') return selectedLot.lot_id != null ? `lot:${selectedLot.lot_id}` : null;
    const unitId = selectedLot.user_lot_id ?? selectedLot.id;
    return unitId != null ? `unit:${unitId}` : null;
  }
  if (locatedUserLotId != null) return `unit:${locatedUserLotId}`;
  if (locatedLotId != null) return `lot:${locatedLotId}`;
  return null;
}
